import { useCallback, useEffect, useState } from 'react';
import mabarService from '@/services/mabarService';
import communityService, { MabarEvent, MabarParticipant } from '@/services/communityService';
import padelService, { PadelSessionListItem, PadelStandingItem } from '@/services/padelService';
import { flattenPadelGames } from '@/components/community/padel/padelListUtils';
import { getApiErrorMessage } from '@/utils/apiErrorMessage';

export function usePadelStatistik(slug: string | undefined, isClientView: boolean) {
  const [event, setEvent] = useState<MabarEvent | null>(null);
  const [sessions, setSessions] = useState<PadelSessionListItem[]>([]);
  const [standings, setStandings] = useState<PadelStandingItem[]>([]);
  const [participants, setParticipants] = useState<MabarParticipant[]>([]);
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  const loadData = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    try {
      const [sessData, standData, access] = await Promise.all([
        padelService.listSessions(slug),
        padelService.getStandings(slug),
        padelService.getAccess(slug),
      ]);
      if (isClientView) {
        const ev = await mabarService.getEventBySlug(slug);
        setEvent(
          ev
            ? ({
                name: ev.name,
                event_date: ev.event_date,
                start_time: ev.start_time,
                end_time: ev.end_time,
                address: ev.address,
                lokasi_arena: ev.lokasi_arena,
              } as MabarEvent)
            : null
        );
        setParticipants([]);
      } else {
        const [eventData, partRes] = await Promise.all([
          communityService.getEventBySlug(slug),
          communityService.getEventParticipants(slug, { status: 'confirmed', limit: 200 }),
        ]);
        setEvent(eventData);
        setParticipants(partRes.participants ?? []);
      }
      setSessions(sessData ?? []);
      setStandings(standData ?? []);
      setCanEdit(access.canEdit);
      setErrorMessage('');
    } catch (err: unknown) {
      setErrorMessage(getApiErrorMessage(err, 'Gagal memuat data'));
    } finally {
      setLoading(false);
    }
  }, [slug, isClientView]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const confirmedParticipants = participants.filter((participant) => participant.status === 'confirmed');
  const flatGames = flattenPadelGames(sessions);

  return {
    event,
    sessions,
    standings,
    participants,
    canEdit,
    loading,
    errorMessage,
    confirmedParticipants,
    flatGames,
    loadData,
  };
}
